import { useMemo, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router";

import { ConfirmModal, DescriptionList, Modal, Spinner } from "@prequel/shared-components";

import { useOrg } from "@/api/queries/orgs";
import { useDeleteSource, useSourceForm, useSourceVendors } from "@/api/queries/sources";
import DangerZone from "@/components/DangerZone";
import Timestamp from "@/components/Timestamp";
import VendorLogo from "@/components/VendorLogo";
import { hiddenSecretValue, isSecretField } from "@/types";
import ExistingSource from "@/types/sources";
import { prepareSourceFromExisting } from "@/types/sources";
import { composeForm } from "@/types/sources/form";

type SourceDetailsProps = {
  source: ExistingSource;
};

const SourceDetails = () => {
  const { source: existingSource } = useOutletContext<SourceDetailsProps>();
  const { sourceID } = useParams<{ sourceID: string }>();
  const navigate = useNavigate();

  const { data: org } = useOrg();
  const { data: sourceForm } = useSourceForm(org?.id);
  const { data: sourceVendors } = useSourceVendors();
  const deleteSourceMutation = useDeleteSource();

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const source = prepareSourceFromExisting(existingSource);

  const sourceVendor = sourceVendors?.find(({ key }) => key === source?.vendor);

  const form = useMemo(() => {
    if (sourceForm) {
      return composeForm(source, sourceForm, !!existingSource);
    }
  }, [source, sourceForm, existingSource]);

  const onDelete = () => setShowDeleteModal(true);

  const onConfirmDelete = () => {
    setShowDeleteModal(false);
    if (existingSource) {
      deleteSourceMutation.mutate({
        sourceId: existingSource.id,
        redirectPath: "/sources",
      });
    }
  };

  const dangerZoneActions = [
    {
      label: "Edit source",
      description: "Edit source fields. Source 'vendor and host' cannot be changed.",
      buttonText: "Edit source",
      onClick: () => navigate(`/sources/${sourceID}/edit`),
    },
    {
      label: "Delete source",
      description: "Once you delete a source, the action cannot be undone.",
      buttonText: "Delete source",
      onClick: onDelete,
    },
  ];

  if (!source || !form) {
    return <Spinner />;
  }

  return (
    <>
      <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <ConfirmModal
          title="Delete source"
          message="Are you sure you want to delete this source? This action cannot be undone."
          onConfirm={onConfirmDelete}
          confirmButtonText="Delete"
          onCancel={() => setShowDeleteModal(false)}
        />
      </Modal>
      <div className="space-y-1">
        <h3 className="text-lg/6 font-medium text-gray-900">Details</h3>
      </div>
      <div className="mt-6">
        {form && source && (
          <DescriptionList>
            <DescriptionList.Item label="ID" value={existingSource.id} />
            {sourceVendor && (
              <DescriptionList.Item
                label="Vendor"
                value={
                  <div className="flex items-center">
                    <VendorLogo logo_url={sourceVendor.icon_url ?? ""} />
                    {sourceVendor.display}
                  </div>
                }
              />
            )}
            {form.map((section) =>
              section.fields.map((field) => {
                if (field.const) {
                  return;
                }

                if (isSecretField(field)) {
                  return (
                    field.label && (
                      <DescriptionList.Item key={field.name} label={field.label} value={hiddenSecretValue} />
                    )
                  );
                }

                if (field.name === "use_ssh_tunnel") {
                  return (
                    <DescriptionList.Item
                      key={field.name}
                      label={"Use SSH Tunnel"}
                      value={source[field.name] ? "Yes" : "No"}
                    />
                  );
                }

                if (field.name === "disable_ssl") {
                  return (
                    <DescriptionList.Item
                      key={field.name}
                      label={field.label}
                      value={source[field.name] ? "Yes" : "No"}
                    />
                  );
                }

                if (field.name === "public_key") {
                  return (
                    <DescriptionList.Item
                      key={field.name}
                      label={field.label}
                      value={
                        <div
                          className="
                            mt-1 inline-flex w-full items-start rounded-md border border-gray-300 bg-gray-50 py-2 pr-1
                            pl-3 font-mono text-sm text-gray-500 shadow-sm
                          "
                        >
                          {source[field.name] ?? ""}
                        </div>
                      }
                    />
                  );
                }

                return (
                  field.label && (
                    <DescriptionList.Item key={field.name} label={field.label} value={source[field.name]} />
                  )
                );
              }),
            )}
            {existingSource?.created_at && (
              <DescriptionList.Item label="Created" value={<Timestamp timestamp={existingSource.created_at} />} />
            )}
            {existingSource?.updated_at && (
              <DescriptionList.Item label="Updated" value={<Timestamp timestamp={existingSource.updated_at} />} />
            )}
          </DescriptionList>
        )}
      </div>
      <DangerZone actions={dangerZoneActions} />
    </>
  );
};

export default SourceDetails;
